import { useCallback, useEffect, useState } from 'react'
import { platformApi } from '../features/platform/public'

type CampaignSummary = {
  id: string
  name: string
  brand_name: string
  status: string
  starts_at?: string | null
  ends_at?: string | null
  timezone: string
  currency: string
}

type Progress = {
  total?: number
  confirmed?: number
  active?: number
  completed?: number
  total_tasks?: number
  awaiting_submission?: number
  submitted?: number
  revision_requested?: number
  approved?: number
}

type Deliverable = {
  id: string
  name: string
  type: string
  default_due_at?: string | null
  requires_draft: boolean
}

type ClientDashboardData = {
  campaign: CampaignSummary
  visible_fields: string[]
  description?: string | null
  creator_progress?: Progress
  submission_progress?: Progress
  deliverables?: Deliverable[]
  exceptions_summary?: {
    open: number
    acknowledged: number
    high_or_critical: number
  }
}

const statusLabels: Record<string, string> = {
  draft: 'Bản nháp',
  configured: 'Đã cấu hình',
  active: 'Đang chạy',
  paused: 'Tạm dừng',
  completed: 'Hoàn thành',
  archived: 'Lưu trữ',
}

function formatDate(value?: string | null) {
  if (!value) return 'Chưa đặt'
  return new Date(value).toLocaleString('vi-VN')
}

function DashboardMetric({ label, value }: { label: string; value: number }) {
  return <div className="metric"><span>{label}</span><strong>{value}</strong></div>
}

export default function ClientDashboard({ campaignId }: { campaignId: string }) {
  const [data, setData] = useState<ClientDashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    const result = await platformApi.clientDashboard.get(campaignId)
    if (result.error) {
      setData(null)
      setError(result.error.message)
    } else {
      setData(result.data as ClientDashboardData)
    }
    setLoading(false)
  }, [campaignId])

  useEffect(() => { void load() }, [load])

  if (loading) return <section id="operations" className="panel"><h2>Đang tải client dashboard…</h2></section>

  if (error || !data) {
    return <section id="operations" className="panel">
      <p className="eyebrow">CLIENT VIEWER</p>
      <h2>Không tải được dashboard</h2>
      <div className="notice error">{error || 'Không có dữ liệu được cấp quyền.'}</div>
      <button onClick={() => void load()}>Thử lại</button>
    </section>
  }

  const creator = data.creator_progress
  const submission = data.submission_progress

  return <section id="operations" className="panel">
    <div className="panel-header">
      <div>
        <p className="eyebrow">CLIENT VIEWER · READ ONLY</p>
        <h2>{data.campaign.name}</h2>
        <p className="muted">{data.campaign.brand_name} · {statusLabels[data.campaign.status] ?? data.campaign.status}</p>
      </div>
      <button onClick={() => void load()}>Làm mới</button>
    </div>

    <div className="notice" role="status">
      Dashboard chỉ hiển thị các trường agency đã whitelist. Contact, fee, internal note, signed link, nội dung submission và review comment không được trả về.
    </div>

    <div className="two-column" style={{ marginTop: '1rem' }}>
      <div>
        <p className="eyebrow">TIMELINE</p>
        <p><strong>Bắt đầu:</strong> {formatDate(data.campaign.starts_at)}</p>
        <p><strong>Kết thúc:</strong> {formatDate(data.campaign.ends_at)}</p>
        <p><strong>Múi giờ:</strong> {data.campaign.timezone}</p>
      </div>
      <div>
        <p className="eyebrow">PHẠM VI HIỂN THỊ</p>
        <p className="muted">{data.visible_fields.length > 0 ? data.visible_fields.join(', ') : 'Chỉ thông tin campaign cơ bản'}</p>
      </div>
    </div>

    {data.description && <div style={{ marginTop: '1rem' }}><p className="eyebrow">MÔ TẢ CAMPAIGN</p><p>{data.description}</p></div>}

    {creator && <div style={{ marginTop: '1rem' }}>
      <p className="eyebrow">CREATOR PROGRESS</p>
      <div className="metric-grid">
        <DashboardMetric label="Tổng creator" value={creator.total ?? 0} />
        <DashboardMetric label="Đã xác nhận" value={creator.confirmed ?? 0} />
        <DashboardMetric label="Đang hoạt động" value={creator.active ?? 0} />
        <DashboardMetric label="Hoàn thành" value={creator.completed ?? 0} />
      </div>
    </div>}

    {submission && <div style={{ marginTop: '1rem' }}>
      <p className="eyebrow">SUBMISSION PROGRESS</p>
      <div className="metric-grid">
        <DashboardMetric label="Tổng task" value={submission.total_tasks ?? 0} />
        <DashboardMetric label="Chờ nộp" value={submission.awaiting_submission ?? 0} />
        <DashboardMetric label="Đã nộp" value={submission.submitted ?? 0} />
        <DashboardMetric label="Đã duyệt" value={submission.approved ?? 0} />
      </div>
      {(submission.revision_requested ?? 0) > 0 && <p className="muted">Đang yêu cầu chỉnh sửa: {submission.revision_requested}</p>}
    </div>}

    {data.deliverables && <div style={{ marginTop: '1rem' }}>
      <p className="eyebrow">DELIVERABLES</p>
      {data.deliverables.length === 0 ? <p className="muted">Chưa có deliverable được hiển thị.</p> : <div className="campaign-list">
        {data.deliverables.map((item) => <article className="campaign-row" key={item.id}>
          <div><h3>{item.name}</h3><p>{item.type} · Deadline mặc định: {formatDate(item.default_due_at)}</p></div>
          <span className="role-chip">{item.requires_draft ? 'Cần draft' : 'Không cần draft'}</span>
        </article>)}
      </div>}
    </div>}

    {data.exceptions_summary && <div style={{ marginTop: '1rem' }}>
      <p className="eyebrow">EXCEPTION SUMMARY</p>
      <div className="metric-grid">
        <DashboardMetric label="Đang mở" value={data.exceptions_summary.open} />
        <DashboardMetric label="Đã tiếp nhận" value={data.exceptions_summary.acknowledged} />
        <DashboardMetric label="Mức cao/nghiêm trọng" value={data.exceptions_summary.high_or_critical} />
      </div>
    </div>}
  </section>
}
